import React, { useEffect, useState } from 'react';
import { Image } from 'react-native';

/**
 * Renders an org logo (base64 data URL) at its true aspect ratio, bounded by a
 * max height + width, and LEFT-aligned (so it lines up with the address lines
 * beneath it on an envelope/statement). A fixed-size box with resizeMode
 * "contain" would letterbox the image and visually center it, leaving the logo
 * indented relative to the address — measuring the natural size avoids that.
 *
 * While measuring (or if the size can't be read), it falls back to a contained
 * box at the max dimensions so the logo still shows on the very first paint —
 * important because the print pages auto-fire window.print() shortly after mount.
 */
interface Props {
	uri: string;
	maxHeight: number;
	maxWidth: number;
}

export const LogoImage: React.FC<Props> = ({ uri, maxHeight, maxWidth }) => {
	const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);

	useEffect(() => {
		let active = true;
		setNatural(null);
		Image.getSize(uri, (w, h) => { if (active && w > 0 && h > 0) setNatural({ w, h }); }, () => {});
		return () => { active = false; };
	}, [uri]);

	if (!natural) {
		return <Image source={{ uri }} style={{ width: maxWidth, height: maxHeight }} resizeMode="contain" />;
	}
	const ratio = natural.w / natural.h;
	let h = maxHeight;
	let w = maxHeight * ratio;
	if (w > maxWidth) { w = maxWidth; h = maxWidth / ratio; }
	return <Image source={{ uri }} style={{ width: w, height: h }} resizeMode="contain" />;
};

export default LogoImage;
